import {
  CalendarDotsIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ListBulletsIcon,
  LockSimpleIcon,
  WarningIcon,
} from '@phosphor-icons/react'
import moment from 'moment'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { SegmentedControl, type SegmentedOption } from 'app/components/action-center/SegmentedControl'
import LocationSelector from 'app/components/analytics/LocationSelector'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import Spinner from 'app/components/Spinner'
import { Button } from 'app/components/ui/button'
import { ControlledSearch } from 'app/components/ui/controlled-search'
import { Label } from 'app/components/ui/label'
import { Switch } from 'app/components/ui/switch'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useDocumentTitle } from 'app/hooks/useDocumentTitle'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { usePmsIntegration } from 'app/hooks/usePmsIntegration'
import type { CalendarView, ColumnGrouping } from 'app/types/appointment-calendar'
import { rangeHeading } from 'lib/appointmentCalendar'
import { CalendarAgenda } from './CalendarAgenda'
import { CalendarDayBoard } from './CalendarDayBoard'
import { CalendarMonth } from './CalendarMonth'
import { VisitLegend } from './CalendarVisit'
import { CalendarWeek } from './CalendarWeek'
import { type AppointmentsCalendarState, useAppointmentsCalendar } from './useAppointmentsCalendar'

const TITLE = `Calendar`

const VIEW_OPTIONS: SegmentedOption<CalendarView>[] = [
  { id: `day`, label: `Day` },
  { id: `week`, label: `Week` },
  { id: `month`, label: `Month` },
]

const GROUPING_OPTIONS: SegmentedOption<ColumnGrouping>[] = [
  { id: `room`, label: `Rooms`, title: `A column per operatory, as the practice books them` },
  { id: `provider`, label: `Providers`, title: `A column per provider in the chair` },
]

const STEP_LABEL: Readonly<Record<CalendarView, string>> = { day: `day`, week: `week`, month: `month` }

function Toolbar({ calendar }: { calendar: AppointmentsCalendarState }): ReactElement {
  const onToday = calendar.range.days.includes(calendar.today)
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      {/* First in the row, where the eye starts: a scope switch parked at the far edge goes unnoticed. */}
      <SegmentedControl
        options={VIEW_OPTIONS}
        value={calendar.view}
        onSelect={calendar.setView}
        className="h-10 max-md:w-full max-md:[&>button]:h-9 max-md:[&>button]:flex-1 max-md:[&>button]:justify-center md:h-8"
      />
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="white"
          size="icon-sm"
          className="size-10 md:size-8"
          aria-label={`Previous ${STEP_LABEL[calendar.view]}`}
          onClick={() => calendar.step(-1)}
        >
          <CaretLeftIcon size={14} weight="bold" />
        </Button>
        <Button
          type="button"
          variant="white"
          size="sm"
          className="h-10 md:h-8"
          disabled={onToday}
          onClick={() => calendar.goTo(calendar.today)}
        >
          Today
        </Button>
        <Button
          type="button"
          variant="white"
          size="icon-sm"
          className="size-10 md:size-8"
          aria-label={`Next ${STEP_LABEL[calendar.view]}`}
          onClick={() => calendar.step(1)}
        >
          <CaretRightIcon size={14} weight="bold" />
        </Button>
      </div>
      <h2 className="flex min-w-0 flex-1 items-center gap-2 text-heading-xs text-strong md:text-heading-sm">
        <span className="truncate">{rangeHeading(calendar.view, calendar.day)}</span>
        {calendar.loading && !calendar.firstLoad && <Spinner size={4} className="size-4 shrink-0" />}
      </h2>
    </div>
  )
}

function Summary({ calendar }: { calendar: AppointmentsCalendarState }): ReactElement {
  const { bookedCount, calledOffCount } = calendar
  const isMobile = useIsMobile()
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-body-sm">
      <span className="text-default">
        <span className="font-semibold tabular-nums text-strong">{calendar.firstLoad ? `...` : bookedCount}</span>
        {` `}
        {bookedCount === 1 ? `visit` : `visits`}
      </span>
      {calendar.view !== `month` && <VisitLegend />}
      {calendar.view === `day` && !isMobile && (
        <SegmentedControl options={GROUPING_OPTIONS} value={calendar.grouping} onSelect={calendar.setGrouping} />
      )}
      {calendar.roomsMissing && !isMobile && (
        <span className="text-body-xs text-subtle">Rooms could not be loaded, so columns are by provider.</span>
      )}
      {calendar.view !== `month` && calledOffCount > 0 && (
        <span className="flex items-center gap-2">
          <Switch
            id="calendar-called-off"
            checked={calendar.showCalledOff}
            onCheckedChange={calendar.setShowCalledOff}
          />
          <Label htmlFor="calendar-called-off" className="text-body-sm font-normal">
            Show {calledOffCount} cancelled or broken
          </Label>
        </span>
      )}
      {calendar.view !== `month` && (
        <ControlledSearch
          placeholder="Find a patient"
          value={calendar.query}
          onValueChange={calendar.setQuery}
          className="w-full md:ml-auto md:w-56"
        />
      )}
    </div>
  )
}

function EmptyCalendar({ calendar }: { calendar: AppointmentsCalendarState }): ReactElement {
  const { view, query, calledOffCount, showCalledOff } = calendar
  const when = view === `day` ? moment(calendar.day).format(`dddd, MMMM D`) : `this week`
  const description = query
    ? `No patient booked ${view === `day` ? `on ${when}` : when} matches "${query}".`
    : calledOffCount && !showCalledOff
      ? `${calledOffCount} cancelled or broken ${calledOffCount === 1 ? `visit is` : `visits are`} hidden.`
      : `Your practice management system has nothing on the books for ${view === `day` ? `this day` : `this week`}.`
  return (
    <div className="flex min-h-0 flex-1 rounded-lg border border-default bg-surface">
      <TableEmptyState
        className="w-full"
        icon={<CalendarDotsIcon className="h-6 w-6" />}
        title={view === `day` ? `Nothing booked on ${when}` : `Nothing booked ${when}`}
        description={description}
        action={
          !query && calledOffCount > 0 && !showCalledOff ? (
            <Button type="button" variant="white" size="sm" onClick={() => calendar.setShowCalledOff(true)}>
              Show them
            </Button>
          ) : undefined
        }
      />
    </div>
  )
}

function CalendarBody({ calendar }: { calendar: AppointmentsCalendarState }): ReactElement {
  const isMobile = useIsMobile()
  if (calendar.failed) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-warning-minimal px-3 py-2 text-body-sm text-warning-strong">
        <WarningIcon size={14} weight="fill" />
        The appointments could not be loaded, so this is not an empty calendar.
        <Button type="button" variant="white" size="sm" onClick={calendar.retry}>
          Try again
        </Button>
      </div>
    )
  }
  if (calendar.firstLoad) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={6} />
      </div>
    )
  }
  if (calendar.view === `month`) return <CalendarMonth calendar={calendar} />
  if (!calendar.visits.length) return <EmptyCalendar calendar={calendar} />
  if (isMobile) return <CalendarAgenda calendar={calendar} />
  return calendar.view === `week` ? <CalendarWeek calendar={calendar} /> : <CalendarDayBoard calendar={calendar} />
}

function Calendar(): ReactElement {
  useDocumentTitle(TITLE)
  const isMobile = useIsMobile()
  const calendar = useAppointmentsCalendar()
  const { location } = calendar
  return (
    <PageScroll
      variant="table"
      header={
        <PageHeader
          title={TITLE}
          otherButtons={[
            <LocationSelector
              key="location"
              selectedOptions={location.selectedOptions}
              locationOptions={location.locationOptions}
              deselectAllOptions={location.deselectAllOptions}
              onChange={location.onLocationChange}
              hideLabel={isMobile}
            />,
            <Button key="list" asChild variant="white" size="sm">
              <Link to="/appointments">
                <ListBulletsIcon className="h-4 w-4" />
                List
              </Link>
            </Button>,
          ]}
        />
      }
      contentClassName="gap-3"
    >
      <Toolbar calendar={calendar} />
      <Summary calendar={calendar} />
      <CalendarBody calendar={calendar} />
      {/* Keyed so a second patient opened from the calendar starts the drawer fresh. */}
      <ContactDrawer key={calendar.drawer.contact?.id} {...calendar.drawer} />
    </PageScroll>
  )
}

function LockedCalendar(): ReactElement {
  useDocumentTitle(TITLE)
  return (
    <PageScroll variant="table" header={<PageHeader title={TITLE} />}>
      <TableEmptyState
        icon={<LockSimpleIcon className="h-6 w-6" />}
        title="The calendar needs a PMS integration"
        description="Appointments come from your practice management system, and none of this workspace's locations has an active PMS integration yet."
      />
    </PageScroll>
  )
}

export function AppointmentsCalendar(): ReactElement | null {
  const { isIntegrated } = usePmsIntegration()
  if (isIntegrated === undefined) return null
  return isIntegrated ? <Calendar /> : <LockedCalendar />
}
